// @flow

import React, { PureComponent } from 'react';
import { ContextMenuTrigger, ContextMenu, MenuItem } from 'react-contextmenu';
import type { RouterHistory } from 'react-router';
import { createFragmentContainer, graphql } from 'react-relay';
import { i18n } from 'shared/utils';
import Pill from 'shared/components/common/Pill';
import JobStatusPill from 'shared/components/common/Pill/JobStatusPill';
import Table from 'shared/components/common/Table';
import Icon from 'shared/components/common/Icon';
import EmptyListResults from 'shared/components/common/EmptyListResults';
import AddUpdateJobModal from 'main-app/components/AddUpdateJobModal';
import DeleteJobModal from 'main-app/components/DeleteJobModal';
import moment from 'moment';
import type { ItemLinkedJobsTable_itemLinkedJobEdges as ItemLinkedJobEdgesFragment } from './__generated__/ItemLinkedJobsTable_itemLinkedJobEdges';

type Props = {
  history: RouterHistory,
  itemLinkedJobEdges: ItemLinkedJobEdgesFragment,
  onCreateNew?: void => void,
  emptyListMessage?: string,
  deleteEnabled?: boolean,
  editEnabled?: boolean,
};

type State = {
  updateJobRecord: ?Object,
  deleteJobRecord: ?Object,
};

class ItemLinkedJobsTable extends PureComponent<Props, State> {
  static defaultProps = {
    onCreateNew: false,
    emptyListMessage: null,
    deleteEnabled: false,
    editEnabled: false,
  };

  menuTriggerRefs = {};
  state = {
    updateJobRecord: null,
    deleteJobRecord: null,
  };

  getJobById = (jobId: string) => {
    const { itemLinkedJobEdges } = this.props;

    const job = itemLinkedJobEdges.find(edge => {
      return edge?.node?.id === jobId;
    });

    return job?.node || null;
  };

  handleGoToOrder = (e, data, target) => {
    const { history } = this.props;
    const jobId = target.getAttribute('job-id');
    const job = this.getJobById(jobId);

    if (!job || !job.order) {
      return;
    }

    history.push(`/order/${job.order.id}`);
  };

  handleOpenNewTab = (e, data, target) => {
    const jobId = target.getAttribute('job-id');

    window.open(`/job/${jobId}`);
  };

  handleOpenUpdateModal = (e, data, target) => {
    const jobId = target.getAttribute('job-id');

    this.setState({
      updateJobRecord: this.getJobById(jobId),
    });
  };

  handleOpenDeleteModal = (e, data, target) => {
    const jobId = target.getAttribute('job-id');

    this.setState({
      deleteJobRecord: this.getJobById(jobId),
    });
  };

  handleCloseAllModals = () => {
    this.setState({
      updateJobRecord: null,
      deleteJobRecord: null,
    });
  };

  render() {
    const {
      history,
      emptyListMessage,
      itemLinkedJobEdges,
      onCreateNew,
      deleteEnabled,
      editEnabled,
    } = this.props;
    const { updateJobRecord, deleteJobRecord } = this.state;

    const edges = itemLinkedJobEdges.filter(Boolean).map(a => a.node);

    if (!edges.length) {
      return (
        <EmptyListResults
          graphic="jobs"
          message={
            emptyListMessage ||
            i18n.t(
              `Your job tickets will show here. Create a new job to get started.`,
            )
          }
          onCreateNew={onCreateNew}
          createNewLabel={i18n.t('Create Job Ticket')}
        />
      );
    }

    return (
      <>
        <Table
          data={edges}
          onRowClick={rowData => history.push(`/job/${rowData.id}`)}
          // renderMobileRow={job => <MobileCell job={job} />}
          columns={[
            {
              Header: i18n.t('Job #'),
              accessor: 'jobNumber',
              width: 160,
              headerStyle: {
                textAlign: 'center',
                paddingRight: '32px',
              },
              style: {
                paddingRight: '32px',
              },
              Cell: ({ value, original }: *) => (
                <Pill onClick={() => history.push(`/job/${original.id}`)}>
                  {original.importJobNumber || original.jobNumber}
                </Pill>
              ),
            },
            {
              Header: i18n.t('Project Name'),
              accessor: 'name',
              minWidth: 200,
              Cell: ({ value }) => value || '',
            },
            {
              Header: i18n.t('Customer'),
              accessor: 'order.customer.name',
              maxWidth: 250,
              Cell: ({ value }) => value || '',
            },
            {
              Header: i18n.t('Items'),
              accessor: 'yields.totalCount',
              headerStyle: {
                textAlign: 'center',
              },
              style: {
                textAlign: 'center',
              },
              width: 120,
              Cell: ({ value }) =>
                i18n.t('{{totalItems}} item(s)', {
                  totalItems: value || 0,
                }),
            },
            {
              Header: i18n.t('Status'),
              accessor: 'status',
              width: 90,
              headerStyle: {
                textAlign: 'center',
              },
              Cell: ({ value }: *) => <JobStatusPill status={value} />,
            },
            {
              Header: i18n.t('Created'),
              accessor: 'createdAt',
              width: 120,
              Cell: ({ value }: *) => moment(value).format('MM/DD/YYYY'),
            },
            {
              Header: '',
              accessor: 'id',
              width: 48,
              Cell: ({ value }: *) => (
                <ContextMenuTrigger
                  ref={r => (this.menuTriggerRefs[value] = r)}
                  id="jobs-menu"
                  attributes={{
                    'job-id': value,
                  }}
                >
                  <Icon
                    type="circle-context-menu"
                    size={24}
                    onClick={(e, data, target) => {
                      if (this.menuTriggerRefs[value]) {
                        this.menuTriggerRefs[value].handleContextClick(
                          e,
                          data,
                          target,
                        );
                      }
                    }}
                  />
                </ContextMenuTrigger>
              ),
            },
          ]}
        />
        <ContextMenu id="jobs-menu">
          {editEnabled && (
            <MenuItem onClick={this.handleOpenUpdateModal}>
              {i18n.t('Edit Job Ticket')}
            </MenuItem>
          )}
          <MenuItem onClick={this.handleOpenNewTab}>
            {i18n.t('Open In New Tab')}
          </MenuItem>
          <MenuItem onClick={this.handleGoToOrder}>
            {i18n.t('View Order')}
          </MenuItem>
          {deleteEnabled && (
            <MenuItem onClick={this.handleOpenDeleteModal}>
              {i18n.t('Delete Job')}
            </MenuItem>
          )}
        </ContextMenu>
        {updateJobRecord && (
          <AddUpdateJobModal
            history={history}
            job={updateJobRecord}
            workflow={null}
            onClose={this.handleCloseAllModals}
          />
        )}
        {deleteJobRecord && (
          <DeleteJobModal
            history={history}
            job={deleteJobRecord}
            onClose={this.handleCloseAllModals}
          />
        )}
      </>
    );
  }
}

export default createFragmentContainer(ItemLinkedJobsTable, {
  itemLinkedJobEdges: graphql`
    fragment ItemLinkedJobsTable_itemLinkedJobEdges on ItemLinkedJobEdge
      @relay(plural: true) {
      usageQty
      node {
        id
        jobNumber
        importJobNumber
        name
        status
        createdAt
        yields(first: null) {
          totalCount
        }
        order {
          id
          promisedAt
          customer {
            id
            name
          }
        }
        states {
          id
        }
        ...AddUpdateJobModal_job
        ...DeleteJobModal_job
      }
    }
  `,
});
